import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import { 
  Package, Box, ShoppingBag, Award,
  Factory, Workflow, Shield,
  Recycle, LineChart, Leaf,
  MapPin, Building2, Phone, Mail,
  Instagram, Facebook, Twitter, Linkedin,
  Settings, Cpu, Zap, Target
} from "lucide-react";
import CountUp from '../components/CountUp';
import QuoteRequestForm from '../components/QuoteRequestForm';

const Index = () => {
  useEffect(() => {
    document.documentElement.dir = "rtl";
  }, []);

  const handleScroll = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const products = [
    {
      icon: <Package className="w-12 h-12 text-blue-600" />,
      title: "أكياس البولي إيثيلين",
      description: "أكياس البولي إيثيلين عالية الجودة بمواصفات متنوعة",
      features: ["متوفر بأحجام متعددة", "مقاومة للتمزق", "خيارات صديقة للبيئة"],
      subCategories: ["عالي الكثافة (HD)", "منخفض الكثافة (LD)", "سماكة مخصصة"],
      sizes: ["صغير: 15x20 سم", "متوسط: 25x35 سم", "كبير: 40x60 سم", "كبير جداً: 50x80 سم"],
      colors: ["شفاف", "أبيض", "أسود", "أزرق", "أحمر", "أخضر", "أصفر"]
    },
    {
      icon: <Box className="w-12 h-12 text-blue-600" />,
      title: "منتجات البولي بروبلين", 
      description: "حاويات البولي بروبلين المتينة وحلول التعبئة والتغليف",
      features: ["تصاميم عصرية", "متانة عالية", "معتمدة لملامسة الطعام"],
      subCategories: ["أفلام BOPP", "أكياس منسوجة", "طباعة مخصصة متاحة"],
      sizes: ["حاويات 500 مل", "حاويات 1 لتر", "حاويات 2 لتر", "حاويات 5 لتر"],
      colors: ["شفاف", "أبيض", "أزرق", "أخضر", "ألوان مخصصة متاحة"]
    },
    {
      icon: <ShoppingBag className="w-12 h-12 text-blue-600" />,
      title: "أكياس التسوق والبيع بالتجزئة",
      description: "أكياس التسوق المتميزة للمتاجر وأعمال البيع بالتجزئة",
      features: ["علامة تجارية مخصصة", "متانة عالية", "جودة طباعة متميزة"],
      subCategories: ["أغطية الغسيل", "أغطية النايلون", "لفات السفرة"],
      sizes: ["صغير: 20x25 سم", "متوسط: 30x40 سم", "كبير: 40x50 سم"],
      colors: ["أبيض", "أسود", "بني", "ألوان العلامة التجارية المخصصة"]
    }
  ];

  const features = [
    {
      icon: <Award className="w-8 h-8 text-blue-600" />,
      title: "شهادات الجودة",
      description: "معتمد من الأيزو بمعايير الجودة الدولية"
    },
    {
      icon: <Recycle className="w-8 h-8 text-blue-600" />,
      title: "صديق للبيئة",
      description: "مواد قابلة لإعادة التدوير وممارسات مستدامة"
    },
    {
      icon: <LineChart className="w-8 h-8 text-blue-600" />,
      title: "إدارة الجودة",
      description: "أنظمة ضمان الجودة الشاملة"
    },
    {
      icon: <Leaf className="w-8 h-8 text-blue-600" />,
      title: "الاستدامة",
      description: "ملتزمون بالمسؤولية البيئية"
    }
  ];

  const qualityStandards = [
    {
      icon: <Award className="w-12 h-12 text-blue-600" />,
      title: "أيزو 9001:2015",
      description: "شهادة نظام إدارة الجودة الدولية التي تضمن منتجات وخدمات عالية الجودة باستمرار."
    },
    {
      icon: <Shield className="w-12 h-12 text-blue-600" />,
      title: "معايير سلامة الغذاء",
      description: "مواد معتمدة من إدارة الغذاء والدواء آمنة لملامسة الطعام، وتلبي جميع أنظمة الصحة والسلامة."
    },
    {
      icon: <Recycle className="w-12 h-12 text-blue-600" />,
      title: "الامتثال البيئي",
      description: "عمليات إنتاج صديقة للبيئة مع مواد قابلة لإعادة التدوير وممارسات تصنيع مستدامة."
    },
    {
      icon: <Target className="w-12 h-12 text-blue-600" />,
      title: "اختبار الجودة",
      description: "إجراءات اختبار صارمة في كل مرحلة إنتاج لضمان معايير المتانة والأداء."
    }
  ];

  const technologyFeatures = [
    {
      icon: <Settings className="w-12 h-12 text-blue-600" />,
      title: "آلات متطورة",
      description: "آلات ألمانية وإيطالية متطورة للتصنيع الدقيق وإنتاج الجودة المستمر."
    },
    {
      icon: <Cpu className="w-12 h-12 text-blue-600" />,
      title: "أنظمة آلية",
      description: "خطوط إنتاج آلية بالكامل مع مراقبة في الوقت الفعلي وأنظمة مراقبة الجودة."
    },
    {
      icon: <Zap className="w-12 h-12 text-blue-600" />,
      title: "الابتكار والتطوير",
      description: "استثمار مستمر في البحث والتطوير لتطوير منتجات جديدة وتحسين عمليات التصنيع."
    }
  ];

  const galleryItems = [
    {
      id: 1,
      category: 'products',
      title: 'أكياس البولي إيثيلين',
      image: '/lovable-uploads/c9483b7c-65a3-421f-9f7d-a239aec7df91.png'
    },
    {
      id: 2,
      category: 'factory',
      title: 'خط إنتاج المصنع',
      image: '/lovable-uploads/a363e14f-9bef-4b1f-9e69-52824a5eb919.png'
    },
    {
      id: 3,
      category: 'products',
      title: 'حاويات بلاستيكية متنوعة',
      image: '/lovable-uploads/30868187-d326-4652-b3af-7a0c963ca9bb.png'
    },
    {
      id: 4,
      category: 'factory',
      title: 'معدات التصنيع الحديثة',
      image: '/lovable-uploads/ef0cab00-247c-42aa-80a7-f576ae6a18e5.png'
    },
    {
      id: 5,
      category: 'packaging',
      title: 'حلول التعبئة والتغليف المتطورة',
      image: '/lovable-uploads/be1f56f5-df17-4b8d-82e0-31afe6d9f1e4.png'
    },
    {
      id: 6,
      category: 'packaging',
      title: 'منتجات جاهزة للشحن',
      image: '/lovable-uploads/e2bae37f-200e-43d6-b590-4e94af4789c7.png'
    }
  ];

  const galleryCategories = [
    { id: 'all', name: 'الكل' },
    { id: 'products', name: 'المنتجات' },
    { id: 'factory', name: 'المصنع' },
    { id: 'packaging', name: 'التعبئة والشحن' }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white flex flex-col w-full overflow-x-hidden">
      <Header />
      
      {/* Home Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-gray-50 w-full px-4 py-20 md:py-32">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
              شركة دان لمنتجات البلاستيك
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              الشركة الرائدة في تصنيع منتجات البلاستيك عالية الجودة في المملكة العربية السعودية
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 px-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => handleScroll('products')}
                className="w-full sm:w-auto bg-[#274f88] text-white px-8 py-3 rounded-lg hover:bg-[#1e3d6a] transition-colors"
              >
                منتجاتنا
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => handleScroll('quote')}
                className="w-full sm:w-auto border-2 border-[#274f88] text-[#274f88] px-8 py-3 rounded-lg hover:bg-[#274f88] hover:text-white transition-colors"
              >
                طلب عرض سعر
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-32 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">حول شركة دان للبلاستيك</h2>
            <div className="w-24 h-1.5 bg-blue-600 rounded-full mx-auto mb-10"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              منذ عام 1989، كانت شركة دان للبلاستيك في طليعة تصنيع البلاستيك في المملكة العربية السعودية، 
              منتجة منتجات البولي إيثيلين والبولي بروبلين عالية الجودة بدقة وتميز.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">مساحة المصنع</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">10,000</p>
              <p className="text-gray-600">متر مربع من مرافق التصنيع الحديثة</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Factory className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">الطاقة الإنتاجية</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">5,000</p>
              <p className="text-gray-600">طن سنوياً مع إمكانية التوسع</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">الخبرة</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">35+</p>
              <p className="text-gray-600">سنة من التميز في تصنيع البلاستيك</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">معايير الجودة</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">أيزو</p>
              <p className="text-gray-600">معتمد بمعايير الجودة الدولية</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">رسالتنا</h3>
                <p className="text-gray-700">تقديم حلول بلاستيكية مبتكرة وعالية الجودة مع الحفاظ على المسؤولية البيئية وتجاوز توقعات العملاء.</p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">رؤيتنا</h3>
                <p className="text-gray-700">أن نكون الشركة الرائدة في تصنيع البلاستيك في المنطقة، معروفة بالجودة والابتكار والاستدامة.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-2xl overflow-hidden shadow-2xl"
                >
                  <img 
                    src="/lovable-uploads/a363e14f-9bef-4b1f-9e69-52824a5eb919.png" 
                    alt="مصنع دان للبلاستيك" 
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">منتجاتنا</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">اكتشف مجموعتنا الشاملة من منتجات البلاستيك عالية الجودة المصممة لتلبية جميع احتياجاتك في التعبئة والتخزين</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="h-32 md:h-40 relative overflow-hidden bg-blue-50 flex items-center justify-center">
                  {product.icon}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{product.title}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">الخيارات المتاحة:</h4>
                    <ul className="space-y-1 text-gray-600">
                      {product.subCategories.map((sub, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {sub}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">الأحجام المتاحة:</h4>
                    <ul className="space-y-1 text-gray-600">
                      {product.sizes.map((size, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-green-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {size}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">الألوان المتاحة:</h4>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {product.colors.map((color, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">المميزات:</h4>
                    <ul className="space-y-2 text-gray-600">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards Section */}
      <section id="quality" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">معايير الجودة والشهادات</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">التزامنا بالتميز ينعكس في التزامنا بمعايير الجودة الدولية والشهادات</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityStandards.map((standard, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center"
              >
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {standard.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{standard.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{standard.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section id="manufacturing" className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">التصنيع والقدرات</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">آلات وتقنيات متطورة لإنتاج الجودة الفائقة</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Factory className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">آلات متطورة</h3>
              <p className="text-gray-600 text-center">أحدث المعدات والتقنيات للتصنيع الدقيق</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• الطاقة الإنتاجية اليومية: 15+ طن</li>
                <li>• إمكانية التشغيل 24/7</li>
                <li>• أنظمة مراقبة الجودة الآلية</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Workflow className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">ضمان الجودة</h3>
              <p className="text-gray-600 text-center">مراقبة جودة شاملة في كل مرحلة إنتاج</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• معتمد أيزو 9001:2015</li>
                <li>• مواد آمنة للطعام</li>
                <li>• إجراءات اختبار صارمة</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">معايير السلامة</h3>
              <p className="text-gray-600 text-center">أعلى بروتوكولات السلامة في جميع عمليات التصنيع</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• امتثال أوشا</li>
                <li>• تدريب السلامة المنتظم</li>
                <li>• حماية البيئة</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">التقنية والابتكار</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">الاستفادة من التقنيات المتطورة والابتكار المستمر لتقديم منتجات وخدمات فائقة</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technologyFeatures.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {tech.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{tech.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Request Section */}
      <QuoteRequestForm />

      {/* Gallery Section */}
      <section id="gallery" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">معرض الصور</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">استعرض مجموعة صورنا التي تعرض منتجاتنا عالية الجودة ومرافق المصنع المتطورة</p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {galleryCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-square cursor-pointer"
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">لماذا تختار شركة دان للبلاستيك</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">نحن ملتزمون بتقديم أفضل المنتجات وأعلى معايير الجودة</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-center">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1">
              <img 
                src="/lovable-uploads/65a27053-2815-4aea-bbd9-bc3d0a0633d1.png" 
                alt="شركة دان للبلاستيك - الشعار" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-right text-sm text-gray-400">
                الشركة الرائدة في تصنيع منتجات البلاستيك عالية الجودة
                <br />
                في المملكة العربية السعودية منذ عام 1989
              </p>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">روابط سريعة</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => handleScroll('about')}
                  className="block text-gray-400 hover:text-white transition-colors text-right"
                >
                  من نحن
                </button>
                <button 
                  onClick={() => handleScroll('products')}
                  className="block text-gray-400 hover:text-white transition-colors text-right"
                >
                  منتجاتنا
                </button>
                <button 
                  onClick={() => handleScroll('manufacturing')}
                  className="block text-gray-400 hover:text-white transition-colors text-right"
                >
                  التصنيع
                </button>
                <button 
                  onClick={() => handleScroll('quote')}
                  className="block text-gray-400 hover:text-white transition-colors text-right"
                >
                  طلب عرض سعر
                </button>
                <button 
                  onClick={() => handleScroll('gallery')}
                  className="block text-gray-400 hover:text-white transition-colors text-right"
                >
                  المعرض
                </button>
              </div>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">منتجاتنا</h3>
              <div className="space-y-2">
                <p className="text-gray-400">أكياس البولي إيثيلين</p>
                <p className="text-gray-400">منتجات البولي بروبلين</p>
                <p className="text-gray-400">أكياس التسوق</p>
                <p className="text-gray-400">التعبئة الصناعية</p>
                <p className="text-gray-400">حلول مخصصة</p>
              </div>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">تواصل معنا</h3>
              <div className="space-y-2">
                <div className="flex items-center text-gray-400">
                  <MapPin className="ml-2 w-5 h-5" />
                  <span>الرياض، المملكة العربية السعودية</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Mail className="ml-2 w-5 h-5" />
                  <span>info@danplastic.com</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Phone className="ml-2 w-5 h-5" />
                  <a 
                    href="tel:920000109" 
                    className="hover:text-white transition-colors"
                  >
                    920000109
                  </a>
                </div>
                
                <div className="pt-4">
                  <h4 className="text-sm font-semibold mb-2">تابعونا</h4>
                  <div className="flex space-x-3 space-x-reverse">
                    <a href="https://www.instagram.com/dan.plastic" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a href="https://www.facebook.com/dan.plastic" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a href="https://x.com/DanPlastic90" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a href="https://www.linkedin.com/company/danplastic/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} شركة دان لمنتجات البلاستيك. جميع الحقوق محفوظة</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
